import React, { lazy, Suspense, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { GrafanaTheme } from '@grafana/data';
import { PluginPage } from '@grafana/runtime';
import { Button, Icon, PageToolbar, TextLink, ToolbarButton, useStyles } from '@grafana/ui';

import { css } from '@emotion/css';

import { useAllHolidays, useMetricJob } from 'api';
import { LoadingIndicator } from 'components/LoadingIndicator';
import { QueryRunnerProvider } from 'components/QueryRunner';
import { PLUGIN_ROOT } from 'consts';
import { useConfirmModal } from 'hooks';
import { useQueryContext } from 'hooks/useQueryContext';
import { DataQueryWithExpression, TenantInfo } from 'types';
import { onHandler } from 'utils';

import { InteractiveForecastDrawer } from '../InteractiveCreate/InteractiveForecastDrawer';

const InteractiveEditJobContent = lazy(() => import('./InteractiveEditJobContent'));

interface Props {
  tenantInfo: TenantInfo;
}

export default function InteractiveEditJob({ tenantInfo }: Props) {
  const styles = useStyles(getStyles);
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, isError } = useMetricJob(id!, { cacheTime: 0 });
  const { data: holidays, isLoading: isHolidaysLoading, isError: isHolidaysError } = useAllHolidays();
  const [showHelp, setShowHelp] = useState(false);
  const navigate = useNavigate();

  const { updateQuery, isEmpty } = useQueryContext();

  useEffect(() => {
    if (isEmpty && data?.queryParams !== undefined) {
      updateQuery(data?.queryParams as DataQueryWithExpression);
    }
  }, [updateQuery, data?.queryParams, isEmpty]);

  const [confirmAbandon, ConfirmAbandon] = useConfirmModal(async () => {
    navigate(`${PLUGIN_ROOT}/metric-forecast`, { replace: true });
  });

  const title = 'Edit metric forecast';
  const actions = (
    <Button onClick={onHandler(confirmAbandon)} variant="destructive" fill="outline">
      Discard
    </Button>
  );
  const subTitle = (
    <p className={styles.subTitle}>
      Make changes to the forecast named &quot;{data?.name}&quot;{' '}
      <TextLink href="#" onClick={() => setShowHelp(true)}>
        <Icon name="question-circle" />
        More info
      </TextLink>
    </p>
  );

  if (isError || isHolidaysError) {
    <PluginPage pageNav={{ text: title }}>
      <div>Error loading forecast</div>
    </PluginPage>;
  }

  if (isLoading || isHolidaysLoading || data == null || holidays == null) {
    return (
      <PluginPage pageNav={{ text: title }} subTitle={subTitle} actions={actions}>
        <div className={styles.wrapper}>
          <PageToolbar className={styles.pageToolbar} data-element="forecasting-create-buttons">
            <ToolbarButton
              id="Save"
              disabled
              variant="primary"
              title="Create forecast and go back to list of forecasts"
            >
              Save
            </ToolbarButton>
            <ToolbarButton id="Cancel" disabled title="Undo all changes">
              Cancel
            </ToolbarButton>
          </PageToolbar>
          <div className={styles.verticalSplitPanesWrapper}>
            <LoadingIndicator />
          </div>
        </div>
        {showHelp && <InteractiveForecastDrawer onClose={() => setShowHelp(false)} editJobId={id} />}
      </PluginPage>
    );
  }

  return (
    <PluginPage pageNav={{ text: title }} subTitle={subTitle} actions={actions}>
      <Suspense fallback={<LoadingIndicator />}>
        <QueryRunnerProvider>
          <InteractiveEditJobContent originalJob={data} allHolidays={holidays} tenantInfo={tenantInfo} />
        </QueryRunnerProvider>
        {showHelp && <InteractiveForecastDrawer onClose={() => setShowHelp(false)} editJobId={id} />}

        <ConfirmAbandon icon="exclamation-triangle" title="Unsaved changes" actionText="Yes">
          <div>Are you sure you want to discard changes to this forecast?</div>
        </ConfirmAbandon>
      </Suspense>
    </PluginPage>
  );
}

const getStyles = (theme: GrafanaTheme) => {
  return {
    wrapper: css`
      z-index: ${theme.zIndex.navbarFixed};
      top: 0;
      bottom: 0;
      right: 0;
      left: 60px;
      background: ${theme.colors.dashboardBg};
      display: flex;
      flex-direction: column;
    `,
    verticalSplitPanesWrapper: css`
      display: flex;
      flex-direction: column;
      position: relative;
      min-height: 300px;
      height: calc(100vh - 350px);
    `,
    pageToolbar: css({
      backgroundColor: 'unset',
      padding: 'unset',
      marginBottom: '16px',
    }),
    subTitle: css`
      display: flex;
      gap: ${theme.spacing.sm};
    `,
  };
};
